import React, { useState } from 'react';
import {
  ArrowLongLeftIcon,
  MagnifyingGlassIcon,
} from '@heroicons/react/24/solid';
import { mentorsData } from './data/MentorsData';

// Mentor Card Component
const MentorCard = ({ mentor }) => (
  <figure className="relative cursor-pointer border border-1 border-dark-grey-4 rounded-lg overflow-hidden">
    <a href={mentor.link} className="block">
      <img
        className="w-full h-80 object-cover"
        src={mentor.image}
        alt={`${mentor.name} - mentor`}
      />
    </a>
    <div className="absolute inset-0 bg-gradient-to-t from-black via-transparent to-transparent"></div>
    <figcaption className="absolute px-4 text-lg text-white bottom-6">
      <h1 className="text-2xl font-bold mb-1">{mentor.name}</h1>
      <p className="text-sm opacity-90">{mentor.title}</p>
    </figcaption>
  </figure>
);

// Main Mentor Grid Component
const MentorGrid = ({
  mentors = mentorsData,
  title = 'Meet our current mentors',
  showSearch = true,
  showAll = false,
  className = '',
}) => {
  const [searchTerm, setSearchTerm] = useState('');

  // Filter mentors based on search term
  const filteredMentors = mentors.filter(
    (mentor) =>
      mentor.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      mentor.title.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const displayedMentors = showAll
    ? filteredMentors
    : filteredMentors.slice(0, 8);

  const handleViewAll = () => {
    window.location.href = '/mentors/all';
  };

  const handleGoBack = () => {
    window.history.back();
  };

  return (
    <section className={`px-6 lg:px-20 mt-10 ${className}`}>
      <div className="mentor-container max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between mb-6 mx-4 lg:mx-2">
          {showAll ? (
            <div className="order-1 sm:order-none flex items-center gap-2">
              <button
                onClick={handleGoBack}
                className="flex items-center gap-2 text-gray-600 hover:text-gray-800 transition-colors"
              >
                <ArrowLongLeftIcon className="w-8 h-8 font-bold" />
                <h1 className="text-lg lg:text-3xl leading-tight text-gray-800 font-semibold">
                  All Mentors ({filteredMentors.length})
                </h1>
              </button>
            </div>
          ) : (
            <h1 className="pt-3 lg:pt-6 lg:max-w-xl text-2xl lg:text-4xl leading-tight text-gray-800 font-semibold text-center sm:text-left">
              {title}
            </h1>
          )}

          {/* Search Bar */}
          {showAll && showSearch && (
            <div className="relative w-full sm:max-w-md order-2 sm:order-none">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <MagnifyingGlassIcon className="h-5 w-5 text-dark-grey-3" />
              </div>
              <input
                type="text"
                placeholder="Search mentors..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="block w-full pl-10 pr-3 py-3 border border-dark-grey-4 rounded-full leading-5 bg-white placeholder-gray-500 focus:outline-none focus:ring-1 focus:ring-primary focus:border-primary"
              />
            </div>
          )}
        </div>

        {/* Mentor Grid */}
        <div className="grid gap-6 grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 py-3">
          {displayedMentors.map((mentor) => (
            <MentorCard key={mentor.id} mentor={mentor} />
          ))}
        </div>

        {/* No results message */}
        {showAll && filteredMentors.length === 0 && (
          <div className="text-center py-12">
            <p className="text-gray-500 text-lg">
              No mentors found matching "{searchTerm}"
            </p>
          </div>
        )}

        {/* View All Button */}
        {!showAll && mentors.length > 8 && (
          <div className="flex justify-center mt-8 p-5">
            <button
              onClick={handleViewAll}
              className="px-8 py-3 text-primary rounded-full border border-primary hover:bg-primary hover:text-white transition-colors"
            >
              View All Mentors ({mentors.length})
            </button>
          </div>
        )}
      </div>
    </section>
  );
};

export default MentorGrid;
